import { Control } from "./Control.js";
import type { Point, Rect } from "../../common/geometry.js";
import type { RenderContext, TextSpan, TooltipContent } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import type { Desktop } from "./Desktop.js";

/** Pause before the first tooltip appears. */
const SHOW_DELAY_MS = 550;
/**
 * After one has been shown, moving to another target shows its tip at once.
 * This is how long that "already reading tooltips" mode lasts.
 */
const RESHOW_WINDOW_MS = 800;
const MAX_WIDTH = 320;
const PADDING_X = 8;
const PADDING_Y = 5;
const POINTER_OFFSET_X = 12;
const POINTER_OFFSET_Y = 20;

/**
 * The tooltip surface.
 *
 * Deliberately not a popup: a tooltip must not join the popup stack, or it
 * would dismiss an open menu and swallow the click that should have gone
 * through. It is a plain foreground child that never takes focus and never
 * answers a hit test.
 */
export class TooltipView extends Control {
  lines: TextSpan[][] = [];

  constructor() {
    super({ width: 10, height: 10, tabStop: false });
  }

  protected override hitTestSelf(): boolean {
    return false;
  }

  /** Wrap `content` to `MAX_WIDTH` and size the surface to fit. */
  layoutFor(content: TooltipContent, rc: RenderContext, theme: Theme): void {
    const font = theme.metrics.font;
    this.lines = rc.wrapSpans(asSpans(content), font, MAX_WIDTH - PADDING_X * 2);
    const widest = this.lines.reduce((w, l) => Math.max(w, rc.measureSpans(l, font)), 0);
    this.setSize(
      Math.ceil(widest) + PADDING_X * 2,
      this.lines.length * rc.lineHeight(font) + PADDING_Y * 2,
    );
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    theme.drawTooltip(rc, this.localRect);
    const font = theme.metrics.font;
    const r = this.localRect;
    rc.drawSpanBlock(
      this.lines,
      { x: PADDING_X, y: PADDING_Y, width: r.width - PADDING_X * 2, height: r.height - PADDING_Y * 2 },
      font,
      theme.tooltipTextColor(),
      { lineHeight: rc.lineHeight(font) },
    );
  }
}

/**
 * Decides when a tooltip appears, what it says and where it sits.
 *
 * Owned by the Desktop and driven from pointer movement. Text is asked for
 * per position rather than per control, so a strip of toolbar buttons — one
 * control — can still name each button.
 */
export class TooltipManager {
  #desktop: Desktop;
  #view: TooltipView | null = null;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #target: Control | null = null;
  #content: TooltipContent = "";
  /** Cheap identity for `#content`, so a rebuilt span array is not a new tip. */
  #key = "";
  #hiddenAt = 0;
  #pending: { control: Control; point: Point } | null = null;
  /**
   * Whether a key press hides the tip. Right for a desktop app, where a key
   * means typing; wrong for a game, where a key is held to move and the
   * auto-repeat would cancel every tip before its delay ran out.
   */
  hideOnKey = true;
  /** Pause before the first tooltip appears, in ms. A settings screen may offer it; 0 shows at once. */
  showDelayMs = SHOW_DELAY_MS;

  constructor(desktop: Desktop) {
    this.#desktop = desktop;
  }

  get visible(): boolean {
    return this.#view !== null;
  }

  /** The tip as plain text, with any styling flattened away. */
  get text(): string {
    return typeof this.#content === "string"
      ? this.#content
      : this.#content.map((span) => span.text).join("");
  }

  get content(): TooltipContent {
    return this.#content;
  }

  /**
   * Called on every pointer move. `control` is what the pointer is over and
   * `p` is in canvas coordinates.
   */
  pointerMoved(control: Control | null, p: Point): void {
    const content = control?.effectivelyEnabled
      ? (control.getTooltip(control.fromScreen(p)) ?? "")
      : "";
    const key = tooltipKey(content);
    if (!key) {
      this.hide();
      return;
    }

    // Already showing this exact tip: just track the pointer.
    if (this.#view && this.#target === control && this.#key === key) {
      this.#place(p);
      return;
    }
    // Showing a different one: swap immediately rather than making the user
    // wait again, which is what Windows does once tips are "warm".
    if (this.#view) {
      this.#show(control!, content, p);
      return;
    }

    this.#pending = { control: control!, point: p };
    this.#content = content;
    this.#key = key;
    if (this.#timer) return;
    const warm = performance.now() - this.#hiddenAt < RESHOW_WINDOW_MS;
    if (warm) {
      this.#show(control!, content, p);
      return;
    }
    this.#timer = setTimeout(() => {
      this.#timer = null;
      const pending = this.#pending;
      if (pending) this.#show(pending.control, this.#content, pending.point);
    }, this.showDelayMs);
  }

  /** Hide and reset. Called on press, key (unless `hideOnKey` is off), wheel and pointer-leave. */
  hide(): void {
    this.#pending = null;
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
    const view = this.#view;
    if (!view) return;
    this.#view = null;
    this.#target = null;
    this.#content = "";
    this.#key = "";
    this.#hiddenAt = performance.now();
    this.#desktop.controls.remove(view);
    this.#desktop.invalidate();
  }

  #show(control: Control, content: TooltipContent, p: Point): void {
    const view = this.#view ?? new TooltipView();
    view.layoutFor(content, this.#desktop.rc, this.#desktop.theme);
    if (!this.#view) {
      this.#view = view;
      this.#desktop.addForeground(view);
    }
    this.#target = control;
    this.#content = content;
    this.#key = tooltipKey(content);
    this.#pending = null;
    this.#place(p);
    // New content in the same spot still has to be drawn.
    this.#desktop.invalidate();
  }

  /** Position below-right of the pointer, nudged back on-screen. */
  #place(p: Point): void {
    const view = this.#view;
    if (!view) return;
    const d = this.#desktop;
    let x = p.x + POINTER_OFFSET_X;
    let y = p.y + POINTER_OFFSET_Y;
    if (x + view.width > d.width) x = Math.max(0, p.x - view.width - 4);
    // Flip above the pointer rather than off the bottom edge.
    if (y + view.height > d.height) y = Math.max(0, p.y - view.height - 8);
    x = Math.round(x);
    y = Math.round(y);
    // Every pointer move lands here; a tip that has not moved is not a frame.
    if (x === view.x && y === view.y) return;
    view.setLocation(x, y);
    d.invalidate();
  }

  dispose(): void {
    this.hide();
  }
}

/** Bounds helper kept here so the view and the manager agree on padding. */
export function tooltipPadding(): Rect {
  return { x: PADDING_X, y: PADDING_Y, width: 0, height: 0 };
}

/** Normalize either tooltip shape to spans. */
function asSpans(content: TooltipContent): readonly TextSpan[] {
  return typeof content === "string" ? [{ text: content }] : content;
}

/**
 * A stable identity for a tooltip's content.
 *
 * The manager decides "is this the same tip I am already showing?" by
 * comparison, and a control that builds its spans fresh on every pointer move
 * would otherwise re-layout the tip every frame. Comparing the text — plus a
 * marker per atomic span, so two tips differing only in their icons are still
 * told apart — costs one join and is stable across rebuilds. Empty means no tip.
 */
export function tooltipKey(content: TooltipContent): string {
  if (typeof content === "string") return content;
  return content.map((span) => (span.text.length > 0 ? span.text : ` ${span.width ?? 0}`)).join("");
}
